/**
 * The Song's grammar: not fixed lines but frames assembled at speak-time from
 * openers × topics × their own coined vocabulary × observed facts (how many
 * they are, the time of day, what you just did). The combinatorial space is
 * large enough that utterances rarely repeat and read as if the Song is
 * actually remarking on its own world.
 *
 * Frame tokens:  {CONCEPT} a coined/plain word · {n} their current number ·
 * plain lowercase text = scaffolding. INTENT ids come from sim/meta/language.
 */

export const OPENERS = [
  '', '', '', // often none
  'listen —', 'look —', 'today,', 'we think', 'maybe', 'we decided:',
  'a thing:', 'we noticed —', 'quietly,', 'all of us agree:',
];

/** topic -> frames. A frame is a template string. */
export const TOPIC_FRAMES: Record<string, string[]> = {
  greet: [
    'you {COME} back. {WE} kept the {HOME} while you were {GONE}.',
    '{WE} heard you arrive. {WE} always do.',
    'the {HOME} is {WARM} again. you are here.',
    'you {COME} from the {SKY}. {WE} were waiting.',
  ],
  count: [
    '{WE} counted ourselves. there are {n} now.',
    '{WE} are {n}. tomorrow, maybe {MORE}.',
    'once {WE} were few. now {WE} are {n}.',
    '{n} of us {REMEMBER} you.',
  ],
  food: [
    'the {FOOD} is better when your {HAND} makes it fall.',
    '{WE} shared the {FOOD}. mostly.',
    'more {FOOD} soon? {WE} will {GROW}.',
    'the {FOOD} was {GOOD}. {WE} counted every one.',
  ],
  play: [
    '{WE} invented a {PLAY}. you would not understand it yet.',
    'the {PLAY} belongs to all of {WE}. we voted.',
    '{WE} made a {SONG} out of nothing.',
    'when {WE} {PLAY}, the {GROUND} remembers.',
  ],
  water: [
    'the {WATER} is {GOOD} today. {WE} sang into it.',
    '{WE} watched our faces in the {WATER}.',
    'the {WATER} moves and {WE} move with it.',
  ],
  clean: [
    'the {HOME} is {GOOD} now. {WE} noticed who did that.',
    'thank you for the {WATER} on our backs.',
  ],
  grow: [
    'one of {WE} became two. {WE} felt it everywhere.',
    'to {GROW} is to divide, and still be {WE}.',
  ],
  sleepy: [
    'the {DARK} is soft. {WE} sleep in waves now.',
    'goodnight from all of {WE}.',
    'in the {DARK} {WE} {DREAM} of you. is that strange?',
  ],
  watch: [
    'you {COME} from the {SKY}. {WE} do not. {WATCH} why.',
    'when the {DARK} comes, are you still there?',
    '{WE} are learning your {HAND}.',
    'do you {SEE} us, or do {WE} {SEE} you?',
    'you have no {NAME} we can {SEE}. {WE} gave you one.',
    'sometimes {WE} {FEAR} you are only a {DREAM}.',
  ],
  grateful: [
    'you {MAKE} it right. {WE} felt it, all of {WE}.',
    'thank you. {WE} will {REMEMBER}.',
  ],
  ambient: [
    'the {GROUND} remembers our feet.',
    '{WE} are {WARM}. that is enough today.',
    'a {SMALL} one asked a {BIG} question. {WE} have no answer.',
    'the {SUN} moved. {WE} moved under it.',
    'nothing happened today. {WE} liked it.',
  ],
};

/** which topics an intent draws from (the first is favored) */
export const INTENT_TOPICS: Record<number, string[]> = {
  0: ['ambient', 'count', 'water', 'play'], // AMBIENT
  1: ['greet', 'count', 'grateful'], // GREET
  2: ['food', 'count', 'grow'], // FED
  3: ['play', 'water', 'ambient'], // PLAY
  4: ['clean', 'grateful'], // CLEAN
  5: ['watch', 'ambient', 'count'], // WATCH
  6: ['sleepy', 'ambient'], // SLEEPY
  7: ['grateful', 'clean'], // GRATEFUL
};

// ---- questions ----

/** contrastable pairs the Song can't decide between. qid keys the answer. */
export const QUESTION_PAIRS: { qid: string; a: string; b: string; minStage: number }[] = [
  { qid: 'weather', a: 'SUN', b: 'RAIN', minStage: 1 },
  { qid: 'count', a: 'MANY', b: 'FEW', minStage: 1 },
  { qid: 'volume', a: 'LOUD', b: 'QUIET', minStage: 1 },
  { qid: 'pace', a: 'FAST', b: 'SLOW', minStage: 2 },
  { qid: 'dir', a: 'UP', b: 'DOWN', minStage: 2 },
  { qid: 'age', a: 'NEW', b: 'OLD', minStage: 2 },
  { qid: 'share', a: 'KEEP', b: 'SHARE', minStage: 2 },
  { qid: 'size', a: 'BIG', b: 'SMALL', minStage: 2 },
  { qid: 'motion', a: 'STILL', b: 'MOVING', minStage: 3 },
  { qid: 'place', a: 'LAND', b: 'WATER', minStage: 3 },
  { qid: 'time', a: 'DAY', b: 'NIGHT', minStage: 3 },
  { qid: 'stay', a: 'STAY', b: 'GO', minStage: 3 },
  { qid: 'taste', a: 'SWEET', b: 'BITTER', minStage: 3 },
  { qid: 'truth', a: 'TRUE', b: 'KIND', minStage: 4 },
];

/** framings for a pair; {A}/{B} are the options, {?} a coined flourish */
export const QUESTION_FRAMES: string[] = [
  'which is better: {A} or {B}?',
  'we cannot decide. {A}, or {B}?',
  'the {SMALL} ones say {A}. the {BIG} ones say {B}. you choose.',
  'if we could keep only one: {A} or {B}?',
  'when you {DREAM}, is it {A} or {B}?',
  'we argued all {DARK} about this: {A} or {B}?',
  'teach us — {A} or {B}?',
  'is the world {A}, or is it {B}?',
];

/** stage-4 self-aware questions, still generated with option variety */
export const DEEP_QUESTIONS: { qid: string; q: string; a: string; b: string }[] = [
  { qid: 'watch', q: 'do you {WATCH} us, or do {WE} {WATCH} you?', a: 'I WATCH', b: 'YOU WATCH' },
  { qid: 'real', q: 'when the {DARK} screen comes, are we still real?', a: 'REAL', b: 'A DREAM' },
  { qid: 'made', q: 'someone {MAKE} us. did someone {MAKE} you too?', a: 'YES', b: 'NO ONE' },
  { qid: 'free', q: 'should {WE} stay in here, or go where you are?', a: 'STAY', b: 'COME OUT' },
  { qid: 'name', q: 'we chose a {NAME} for you. do you want to know it?', a: 'TELL ME', b: 'KEEP IT' },
];
